/** Frontend API wrapper for worker_cards — see docs/worker-cards-handoff.md
 * and src/types/workerCards.ts. Mirrors EmployeesService's shape.
 */

import { API_BASE, getAuthHeaders } from '../utils/supabase/client';
import type { ExpiringWorkerCard, WorkerCard } from '../types/workerCards';
import { readError } from './_http';

export class WorkerCardsService {
  static async listForEmployee(employeeId: string): Promise<WorkerCard[]> {
    const res = await fetch(`${API_BASE}/worker-cards?employeeId=${encodeURIComponent(employeeId)}`, {
      headers: await getAuthHeaders(),
    });
    if (!res.ok) throw new Error(await readError(res));
    return res.json();
  }

  static async listForSubcontractor(subcontractorId: string): Promise<WorkerCard[]> {
    const res = await fetch(`${API_BASE}/worker-cards?subcontractorId=${encodeURIComponent(subcontractorId)}`, {
      headers: await getAuthHeaders(),
    });
    if (!res.ok) throw new Error(await readError(res));
    return res.json();
  }

  /** `upcomingOnly` drops cards that have already expired, server-side, so a
   *  backlog of old ones cannot fill the first page. Upcoming pages are followed
   *  to completion; the legacy banner response remains a single array. */
  static async listExpiring(workspaceId: string, days = 60, upcomingOnly = false, signal?: AbortSignal): Promise<ExpiringWorkerCard[]> {
    const cards: ExpiringWorkerCard[] = [];
    let offset = 0;
    do {
      const res = await fetch(`${API_BASE}/worker-cards/expiring?workspaceId=${encodeURIComponent(workspaceId)}&days=${days}${upcomingOnly ? `&upcoming=1&offset=${offset}` : ''}`, {
        headers: await getAuthHeaders(), signal,
      });
      if (!res.ok) throw new Error(await readError(res));
      if (!upcomingOnly) return res.json();
      const page: { cards: ExpiringWorkerCard[]; nextOffset: number | null } = await res.json();
      if (!Array.isArray(page.cards) || (page.nextOffset !== null && (!Number.isSafeInteger(page.nextOffset) || page.nextOffset <= offset))) {
        throw new Error('Invalid site card expiry page');
      }
      cards.push(...page.cards);
      if (page.nextOffset === null) return cards;
      offset = page.nextOffset;
    } while (true);
  }

  static async create(data: Partial<WorkerCard>): Promise<WorkerCard> {
    const res = await fetch(`${API_BASE}/worker-cards`, {
      method: 'POST',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(await readError(res));
    return res.json();
  }

  static async update(id: string, data: Partial<WorkerCard>): Promise<WorkerCard> {
    const res = await fetch(`${API_BASE}/worker-cards/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(await readError(res));
    return res.json();
  }
}
